import { HashLink } from "@/components/HashLink";
import { LoginDialog } from "@/components/LoginDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MEGA_MENU_EXTERNAL_URLS } from "@/i18n/menu-external-urls";
import type { UserInfo } from "@/lib/api/auth";
import { useLang, useT } from "@/lib/i18n";
import type { PlanId } from "@/lib/pricing";
import { products } from "@/lib/sections";
import {
  clearAuth,
  getUserInfo as getCachedUserInfo,
  getToken,
  saveUserInfo,
} from "@/lib/utils/auth-storage";
import { isSubAppKey } from "@/lib/config";
import { captureInviteCodeFromURL } from "@/lib/utils/invite-code";
import { navigateToSubApp } from "@/lib/utils/sso";
import { LogOut, UserRound } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

type LinkItem = { label: string; href: string };
type FlatMenu = { label: string; href?: string; items: LinkItem[] };
type GroupedMenu = {
  label: string;
  href?: string;
  groups: { header: string; items: LinkItem[] }[];
};
type LinkOnlyMenu = { label: string; href: string };
type Menu = FlatMenu | GroupedMenu | LinkOnlyMenu;

interface HeaderProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  currentPlanId?: PlanId;
  sidebarOpen?: boolean;
  setSidebarOpen?: (open: boolean) => void;
  isVisible?: boolean;
  setLibrarySubTab?: (tab: "video" | "voice" | "model") => void;
  loginDialogTrigger?: number;
  /** 由 IndexPage 传入的登录后跳转目标，优先级高于 URL 参数和 sessionStorage。
   *  值为 "back" 或已注册子应用标识（如 "toolbox"、"prompt"）。 */
  postLoginRedirect?: string | null;
}

/**
 * Landing V2 Nav. Preserves the old Header prop signature so IndexPage doesn't need to change.
 * Login is wired through {@link LoginDialog}; everything else (theme toggle, sidebar) was dropped
 * as part of the V2 redesign.
 */
const Header: React.FC<HeaderProps> = ({ loginDialogTrigger = 0, postLoginRedirect }) => {
  const { lang, setLang } = useLang();
  const t = useT();
  // 根据 URL 预判弹窗模式，避免 useEffect 延迟设置导致 LoginDialog 先渲染 signin 再切换 signup
  const getInitialModeFromURL = (): "signin" | "signup" | "forgot" => {
    try {
      const url = new URL(window.location.href);
      if (url.pathname === "/register") return "signup";
    } catch { /* ignore */ }
    return "signin";
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialMode, setDialogInitialMode] = useState<"signin" | "signup" | "forgot">(getInitialModeFromURL());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  // `back` = window.history.back(); 其他值 = 已注册子应用标识（如 "toolbox"、"prompt"）。
  const postLoginRedirectRef = useRef<string | null>(null);
  const prevLoginTriggerRef = useRef(0);

  // Restore user from localStorage on mount (AppProviders has already revalidated the token).
  useEffect(() => {
    const cached = getCachedUserInfo();
    if (cached && getToken()) setUser(cached);
  }, []);

  // Cross-tab sync: when another tab logs in/out, refresh our state.
  useEffect(() => {
    const onStorage = () => {
      const cached = getCachedUserInfo();
      setUser(cached && getToken() ? cached : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Sync postLoginRedirect prop from IndexPage into the ref.
  useEffect(() => {
    if (postLoginRedirect) {
      postLoginRedirectRef.current = postLoginRedirect;
    }
  }, [postLoginRedirect]);

  // 首次挂载时统一处理 URL 深链接（邀请码采集、login、register）
  // 合并为一个 useEffect，避免多次 replaceState + setState 导致的渲染时序问题
  useEffect(() => {
    const url = new URL(window.location.href);
    let needReplace = false;
    let shouldOpenDialog = false;
    let dialogMode: "signin" | "signup" | "forgot" | null = null;

    // 1. 采集邀请码：从 URL 读取并存入 sessionStorage
    if (captureInviteCodeFromURL(url)) {
      needReplace = true;
    }

    // 2. /register?inviteCode=xxx → 自动打开注册弹窗
    if (url.pathname === "/register") {
      dialogMode = "signup";
      shouldOpenDialog = true;
      url.pathname = "/";
      needReplace = true;
    }

    // 3. /?login=1 (back) or /?login=<subAppKey> → 自动打开登录弹窗
    //    已登录用户访问 ?login=<subAppKey> 时直接跳转，无需弹窗
    const loginParam = url.searchParams.get("login");
    if (loginParam === "1") {
      postLoginRedirectRef.current = "back";
      sessionStorage.setItem("loginRedirect", "back");
      shouldOpenDialog = true;
      url.searchParams.delete("login");
      needReplace = true;
    } else if (loginParam && isSubAppKey(loginParam)) {
      // 已登录 → 直接 SSO 跳转，不弹窗
      if (getToken()) {
        url.searchParams.delete("login");
        window.history.replaceState({}, "", url.toString());
        navigateToSubApp(loginParam).catch(() => { /* 前置已校验 token 和 appKey，此处仅兜底 */ });
        return;
      }
      // 未登录 → 记录跳转目标，弹登录窗
      postLoginRedirectRef.current = loginParam;
      sessionStorage.setItem("loginRedirect", loginParam);
      shouldOpenDialog = true;
      url.searchParams.delete("login");
      needReplace = true;
    }

    // 统一一次弹窗，避免步骤 2/3 各自 setDialogOpen(true) 导致重复触发
    if (shouldOpenDialog) {
      if (dialogMode) {
        setDialogInitialMode((prev) => (dialogMode !== prev ? dialogMode : prev));
      }
      setDialogOpen(true);
    }

    // 一次性 replaceState，避免多次修改 URL
    if (needReplace) {
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Pricing page / other child components ask for login by incrementing this counter.
  useEffect(() => {
    if (loginDialogTrigger <= prevLoginTriggerRef.current) return;
    prevLoginTriggerRef.current = loginDialogTrigger;
    setDialogOpen(true);
  }, [loginDialogTrigger]);

  const handleLoginSuccess = (next: UserInfo) => {
    setUser(next);
    const redirect = postLoginRedirectRef.current || sessionStorage.getItem("loginRedirect");
    postLoginRedirectRef.current = null;
    sessionStorage.removeItem("loginRedirect");
    if (!redirect) return;
    // Small delay so the dialog close animation can finish before navigating away.
    setTimeout(async () => {
      if (redirect === "back") {
        window.history.back();
      } else if (isSubAppKey(redirect)) {
        try {
          await navigateToSubApp(redirect);
        } catch {
          // navigateToSubApp 内部已有降级逻辑
        }
      }
    }, 300);
  };

  const handleSignOut = () => {
    setProfileDialogOpen(false);
    clearAuth();
    setUser(null);
  };

  const handleProfileSaved = (nextUser: UserInfo) => {
    setUser(nextUser);
    saveUserInfo(nextUser);
  };

  const getInitials = (name: string) => name.slice(0, 1).toUpperCase();

  const menus: Menu[] = [
    {
      label: t("Products", "产品"),
      href: "/products",
      items: products.map((p) => ({
        label: t(p.en, p.zh),
        href: p.slug === "orangeo"
          ? MEGA_MENU_EXTERNAL_URLS["menu.prod.orangeo"]
          : `/products#${p.slug}`,
      })),
    },
    {
      label: t("Solutions", "解决方案"),
      href: "/solutions",
    },
    {
      label: t("Platform", "平台"),
      href: "/models",
      items: [
        {
          label: t("Data Center", "数据中心"),
          href: MEGA_MENU_EXTERNAL_URLS["menu.plat.dataCenter"],
        },
        {
          label: t("Model Library", "模型库"),
          href: "https://models.photog.art",
        },
        {
          label: t("Skills", "技能"),
          href: MEGA_MENU_EXTERNAL_URLS["menu.plat.skills"],
        },
        { label: t("API Docs", "API 文档"), href: "#" },
      ],
    },
    {
      label: t("Inspiration", "灵感"),
      href: "/library",
      items: [
        { label: t("Video", "视频"), href: "/library?tab=video" },
        { label: t("Voice", "声音"), href: "/library?tab=voice" },
        { label: t("Model", "模特"), href: "/library?tab=model" },
      ],
    },
    {
      label: t("Company", "公司"),
      href: "/company",
      items: [
        { label: t("About", "关于"), href: "/company#about" },
        { label: t("News", "新闻"), href: "/company#" },
        // { label: t("Careers", "招聘"), href: "#" },
        // { label: t("Founding Partner", "先锋伙伴"), href: "#" },
        { label: t("Contact", "联系"), href: "/company#contact" },
      ],
    },
  ];

  const renderItem = (i: LinkItem) => {
    const className =
      "block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors whitespace-nowrap";
    if (i.href === "#") {
      return (
        <a href="#" className={className}>
          {i.label}
        </a>
      );
    }
    if (/^https?:\/\//.test(i.href)) {
      return (
        <a href={i.href} target="_blank" rel="noopener noreferrer" className={className}>
          {i.label}
        </a>
      );
    }
    return (
      <HashLink to={i.href} className={className}>
        {i.label}
      </HashLink>
    );
  };

  const renderTrigger = (m: Menu) => {
    const className = "px-3 py-2 hover:text-foreground transition-colors flex items-center gap-1";
    const inner = (
      <>
        {m.label} <span className="text-xs opacity-60">▾</span>
      </>
    );
    return m.href ? (
      <Link to={m.href} className={className}>
        {inner}
      </Link>
    ) : (
      <button className={className}>{inner}</button>
    );
  };

  return (
    <div className="landing-v2">
      <header className="fixed top-0 inset-x-0 z-50">
        <div className="mx-auto max-w-[min(1760px,95vw)] px-6 mt-4">
          <div className="glass-strong flex items-center justify-between px-5 py-3 rounded-full">
            <Link to="/" className="flex items-center gap-2 font-semibold tracking-wide">
              <img
                src="/logo.svg"
                alt="OranAI"
                className="h-7 w-7 object-contain"
                style={{ filter: "drop-shadow(var(--shadow-glow))" }}
              />
              <span className="text-foreground text-lg font-medium tracking-wide">OranAI</span>
            </Link>
            <nav className="hidden md:flex items-center gap-1 text-sm text-muted-foreground">
              {menus.map((m) =>
                "items" in m || "groups" in m ? (
                  <div key={m.label} className="relative group">
                    {renderTrigger(m as FlatMenu | GroupedMenu)}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 pointer-events-none group-hover:pointer-events-auto">
                      <div
                        className={`glass-strong rounded-2xl p-3 ${
                          "groups" in m ? "min-w-[480px] grid grid-cols-2 gap-6" : "min-w-[200px]"
                        }`}
                      >
                        {"groups" in m ? (
                          (m as GroupedMenu).groups.map((g) => (
                            <div key={g.header}>
                              <div className="px-3 pb-2 text-xs uppercase tracking-wider text-muted-foreground/70">
                                {g.header}
                              </div>
                              <ul className="flex flex-col">
                                {g.items.map((i) => (
                                  <li key={i.label}>{renderItem(i)}</li>
                                ))}
                              </ul>
                            </div>
                          ))
                        ) : (
                          <ul className="flex flex-col">
                            {(m as FlatMenu).items.map((i) => (
                              <li key={i.label}>{renderItem(i)}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={m.label}
                    to={(m as LinkOnlyMenu).href}
                    className="px-3 py-2 hover:text-foreground transition-colors"
                  >
                    {m.label}
                  </Link>
                )
              )}
              <Link
                to="/pricing"
                className="px-3 py-2 hover:text-foreground transition-colors"
              >
                {t("Pricing", "定价")}
              </Link>
            </nav>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center rounded-full border border-border overflow-hidden text-xs">
                <button
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1 transition-colors ${
                    lang === "en" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("zh")}
                  className={`px-2.5 py-1 transition-colors ${
                    lang === "zh" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  中
                </button>
              </div>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      aria-label={t("Account menu", "账号菜单")}
                    >
                      <Avatar className="w-8 h-8 cursor-pointer hover:ring-2 hover:ring-foreground/40 transition-all">
                        {user.avatar && (
                          <AvatarImage src={user.avatar} alt={user.nickname || user.username} />
                        )}
                        <AvatarFallback className="bg-foreground text-background text-sm font-medium">
                          {getInitials(user.nickname || user.username)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="landing-v2 w-72 rounded-2xl border border-border bg-background/95 p-2 shadow-2xl backdrop-blur-md"
                  >
                    <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2 font-normal">
                      <Avatar className="h-10 w-10 border border-border">
                        {user.avatar && (
                          <AvatarImage src={user.avatar} alt={user.nickname || user.username} />
                        )}
                        <AvatarFallback className="bg-foreground text-sm font-medium text-background">
                          {getInitials(user.nickname || user.username)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-foreground">
                          {user.nickname || user.username}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem
                      className="cursor-pointer gap-2 rounded-xl px-3 py-2.5"
                      onSelect={() => setProfileDialogOpen(true)}
                    >
                      <UserRound className="h-4 w-4 shrink-0" aria-hidden />
                      <span>{t("Personal profile", "个人中心")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer gap-2 rounded-xl px-3 py-2.5 text-[#FF6B6B] focus:bg-[#FF4D4F]/15 focus:text-[#FF8585]"
                      onSelect={handleSignOut}
                    >
                      <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                      <span>{t("Sign out", "退出登录")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  type="button"
                  onClick={() => setDialogOpen(true)}
                  className="btn-secondary !py-2 !px-4 text-xs"
                >
                  {t("Sign In", "登录")}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      <LoginDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={handleLoginSuccess} initialMode={dialogInitialMode} />
      {user && (
        <ProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          user={user}
          onSaved={handleProfileSaved}
        />
      )}
    </div>
  );
};

export default Header;
